/**
 * Dense, grouped-by-phase table view of project tasks.
 * Rows support inline editing of status + priority. Clicking the row opens
 * the TaskDrawer (owned by the parent).
 */
import { ChevronDown, ChevronUp, MessageSquare, Paperclip } from "lucide-react";
import { useState } from "react";
import type { ProjectTask } from "@/hooks/use-tasks";
import { useUpdateProjectTask } from "@/hooks/use-tasks";
import {
  formatRelativeDate,
  PriorityChip,
  type ProjectPhase,
  StatusChip,
  TaskAvatar,
  type TaskPriority,
  type TaskStatus,
} from "./task-chips";

// Order in which phase groups are rendered. Any unknown phase is lumped
// into "Other" at the end.
const PHASE_ORDER: ProjectPhase[] = [
  "construction",
  "design",
  "pre_construction",
  "post_construction",
  "completed",
];

const PHASE_LABELS: Record<ProjectPhase, string> = {
  pre_construction: "Pre-Construction",
  design: "Design",
  construction: "Construction",
  post_construction: "Close-out",
  completed: "Completed",
};

interface TaskSummaryCounts {
  task_id: string;
  comment_count: number;
  dep_count: number;
  blocker_count: number;
  checklist_total: number;
  checklist_done: number;
}

function groupTasksByPhase(tasks: ProjectTask[]): Array<[ProjectPhase | "other", ProjectTask[]]> {
  const groups = new Map<ProjectPhase | "other", ProjectTask[]>();
  for (const t of tasks) {
    const key = (PHASE_ORDER as string[]).includes(t.phase ?? "")
      ? ((t.phase ?? "other") as ProjectPhase)
      : "other";
    const arr = groups.get(key);
    if (arr) arr.push(t);
    else groups.set(key, [t]);
  }
  const ordered: Array<[ProjectPhase | "other", ProjectTask[]]> = [];
  for (const p of PHASE_ORDER) {
    const arr = groups.get(p);
    if (arr && arr.length > 0) ordered.push([p, arr]);
  }
  const other = groups.get("other");
  if (other && other.length > 0) ordered.push(["other", other]);
  return ordered;
}

// ── Sorting ──────────────────────────────────────────────────────────

type SortKey = "title" | "status" | "priority" | "due" | "assignee" | "progress";
type SortDir = "asc" | "desc";

const STATUS_RANK: Record<string, number> = {
  in_progress: 0,
  in_review: 1,
  not_started: 2,
  blocked: 3,
  completed: 4,
  skipped: 5,
  overdue: 6,
};
const PRIORITY_RANK: Record<string, number> = { high: 0, medium: 1, low: 2 };

function sortTasks(
  tasks: ProjectTask[],
  key: SortKey,
  dir: SortDir,
  assigneeLabels: Map<string, string>,
): ProjectTask[] {
  const asc = dir === "asc";
  return [...tasks].sort((a, b) => {
    let cmp = 0;
    switch (key) {
      case "title":
        cmp = (a.title ?? "").localeCompare(b.title ?? "");
        break;
      case "status":
        cmp = (STATUS_RANK[a.status ?? ""] ?? 99) - (STATUS_RANK[b.status ?? ""] ?? 99);
        break;
      case "priority":
        cmp = (PRIORITY_RANK[a.priority ?? ""] ?? 99) - (PRIORITY_RANK[b.priority ?? ""] ?? 99);
        break;
      case "due": {
        const ad = a.dueDate ? new Date(a.dueDate).getTime() : Number.MAX_SAFE_INTEGER;
        const bd = b.dueDate ? new Date(b.dueDate).getTime() : Number.MAX_SAFE_INTEGER;
        cmp = ad - bd;
        break;
      }
      case "assignee": {
        const al = resolveAssigneeLabel(a, assigneeLabels) ?? "";
        const bl = resolveAssigneeLabel(b, assigneeLabels) ?? "";
        cmp = al.localeCompare(bl);
        break;
      }
      case "progress":
        cmp = (a.progress ?? 0) - (b.progress ?? 0);
        break;
    }
    return asc ? cmp : -cmp;
  });
}

function averageProgress(tasks: ProjectTask[]): number {
  if (tasks.length === 0) return 0;
  const sum = tasks.reduce((acc, t) => acc + (t.progress ?? 0), 0);
  return Math.round(sum / tasks.length);
}

function TaskRow({
  task,
  counts,
  onOpen,
  projectId,
  contactLabel,
}: {
  task: ProjectTask;
  counts: TaskSummaryCounts | undefined;
  onOpen: (task: ProjectTask) => void;
  projectId: string;
  contactLabel: string | null;
}) {
  const updateTask = useUpdateProjectTask(projectId);
  const due = formatRelativeDate(task.dueDate);
  const dateCls =
    due?.variant === "overdue"
      ? "date overdue"
      : due?.variant === "duesoon"
        ? "date duesoon"
        : "date";
  const progress = task.progress ?? 0;
  const barCls = progress >= 100 ? "bar done" : task.status === "blocked" ? "bar warn" : "bar";

  return (
    <tr onClick={() => onOpen(task)}>
      <td>
        <div className="row-title">
          <span className="tid">{task.id.slice(0, 6).toUpperCase()}</span>
          <span className="ttext" title={task.title ?? ""}>
            {task.title ?? "—"}
          </span>
          {counts?.comment_count ? (
            <span className="tid" title={`${counts.comment_count} comments`}>
              <MessageSquare className="inline h-3 w-3" /> {counts.comment_count}
            </span>
          ) : null}
          {counts?.checklist_total ? (
            <span className="tid">
              <Paperclip className="inline h-3 w-3" /> {counts.checklist_done}/
              {counts.checklist_total}
            </span>
          ) : null}
        </div>
      </td>
      <td onClick={(e) => e.stopPropagation()}>
        <StatusChip
          value={task.status as TaskStatus | null}
          onChange={(next) => updateTask.mutate({ taskId: task.id, status: next })}
        />
      </td>
      <td onClick={(e) => e.stopPropagation()}>
        <PriorityChip
          value={(task.priority ?? "medium") as TaskPriority}
          onChange={(next) => updateTask.mutate({ taskId: task.id, priority: next })}
        />
      </td>
      <td>
        {due ? (
          <span className={dateCls}>
            <span>{due.label}</span>
            <span className="rel">{due.rel}</span>
          </span>
        ) : (
          <span className="text-[var(--text-4)]">—</span>
        )}
      </td>
      <td>
        {contactLabel ? (
          <span className="inline-flex items-center gap-2">
            <TaskAvatar name={contactLabel} keyHint={task.assigneeId ?? contactLabel} />
            <span>{contactLabel}</span>
          </span>
        ) : (
          <span className="text-[var(--text-4)]">Unassigned</span>
        )}
      </td>
      <td>
        {counts?.blocker_count ? (
          <span className="dep blocked">⚑ {counts.blocker_count}</span>
        ) : counts?.dep_count ? (
          <span className="dep">→ {counts.dep_count}</span>
        ) : (
          <span className="text-[var(--text-4)]">—</span>
        )}
      </td>
      <td>
        <div className="progress-cell">
          <span className={barCls}>
            <i style={{ width: `${Math.min(100, Math.max(0, progress))}%` }} />
          </span>
          <span className="mono">{progress}%</span>
        </div>
      </td>
    </tr>
  );
}

export function TasksTable({
  tasks,
  projectId,
  summaryCounts,
  assigneeLabels,
  onOpenTask,
}: {
  tasks: ProjectTask[];
  projectId: string;
  summaryCounts: Map<string, TaskSummaryCounts>;
  assigneeLabels: Map<string, string>;
  onOpenTask: (task: ProjectTask) => void;
}) {
  const [sortKey, setSortKey] = useState<SortKey>("title");
  const [sortDir, setSortDir] = useState<SortDir>("asc");

  function handleSort(key: SortKey) {
    if (key === sortKey) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortKey(key);
      setSortDir("asc");
    }
  }

  function SortIcon({ col }: { col: SortKey }) {
    if (col !== sortKey) return null;
    return sortDir === "asc" ? (
      <ChevronUp className="inline h-3 w-3 ml-0.5 opacity-60" />
    ) : (
      <ChevronDown className="inline h-3 w-3 ml-0.5 opacity-60" />
    );
  }

  const groups = groupTasksByPhase(tasks);
  if (groups.length === 0) {
    return (
      <div className="rounded-lg border border-[var(--border-1)] bg-[var(--paper-0)] p-12 text-center">
        <p className="text-[var(--text-3)] text-sm">No tasks yet.</p>
      </div>
    );
  }

  const thCls = "cursor-pointer select-none hover:text-[var(--text-1)] transition-colors";

  return (
    <div className="rounded-lg border border-[var(--border-1)] overflow-hidden bg-[var(--paper-0)]">
      {groups.map(([phase, rows]) => {
        const label = phase === "other" ? "Other" : PHASE_LABELS[phase];
        const sorted = sortTasks(rows, sortKey, sortDir, assigneeLabels);
        const avg = averageProgress(rows);
        return (
          <div key={phase}>
            <div className="tb-group">
              <span className="tb-group-label">{label}</span>
              <span className="tb-group-count">{rows.length}</span>
              <div className="tb-group-progress">
                <span className="bar">
                  <i style={{ width: `${avg}%` }} />
                </span>
                <span className="mono">{avg}%</span>
              </div>
            </div>
            <table className="tasks">
              <thead>
                <tr>
                  <th style={{ width: "34%" }}>
                    <button type="button" className={thCls} onClick={() => handleSort("title")}>
                      Task <SortIcon col="title" />
                    </button>
                  </th>
                  <th style={{ width: "11%" }}>
                    <button type="button" className={thCls} onClick={() => handleSort("status")}>
                      Status <SortIcon col="status" />
                    </button>
                  </th>
                  <th style={{ width: "10%" }}>
                    <button type="button" className={thCls} onClick={() => handleSort("priority")}>
                      Priority <SortIcon col="priority" />
                    </button>
                  </th>
                  <th style={{ width: "12%" }}>
                    <button type="button" className={thCls} onClick={() => handleSort("due")}>
                      Due <SortIcon col="due" />
                    </button>
                  </th>
                  <th style={{ width: "14%" }}>
                    <button type="button" className={thCls} onClick={() => handleSort("assignee")}>
                      Assignee <SortIcon col="assignee" />
                    </button>
                  </th>
                  <th style={{ width: "8%" }}>Deps</th>
                  <th style={{ width: "11%" }}>
                    <button type="button" className={thCls} onClick={() => handleSort("progress")}>
                      Progress <SortIcon col="progress" />
                    </button>
                  </th>
                </tr>
              </thead>
              <tbody>
                {sorted.map((t) => (
                  <TaskRow
                    key={t.id}
                    task={t}
                    counts={summaryCounts.get(t.id)}
                    onOpen={onOpenTask}
                    projectId={projectId}
                    contactLabel={resolveAssigneeLabel(t, assigneeLabels)}
                  />
                ))}
              </tbody>
            </table>
          </div>
        );
      })}
    </div>
  );
}

function resolveAssigneeLabel(task: ProjectTask, labels: Map<string, string>): string | null {
  const tradeName = task.assignedTradeId ? (labels.get(task.assignedTradeId) ?? null) : null;
  const contactName = task.assignedToContactId
    ? (labels.get(task.assignedToContactId) ?? null)
    : null;
  if (tradeName && contactName) return `${tradeName} · ${contactName}`;
  return tradeName ?? contactName ?? null;
}
